import { type Dispatch, type SetStateAction } from "react";
import { useMutation } from "@tanstack/react-query";
import { notification } from "antd";
import { journalsQueries } from "@/entities/journal";
import { useCoordinator } from "@/shared/lib/hooks";
import { queryClient } from "@/shared/lib/react-query";
import { NOTIFICATION_MESSAGES } from "@/shared/utils";
import { $journalApi } from "@entities/journal/fetch/fetch-client.ts";

interface IRequest {
  administrativeDocumentId: string;
  name: string;
  issueDate: string;
  number: string;
  organization?: string;
  description?: string;
  representative: {
    representativeType:
      | "developer"
      | "technicalCustomer"
      | "buildingContractor"
      | "designerSupervisions"
      | "otherDevelopers"
      | string;
    isConstructionControl: boolean;
    companyId: string;
    projectParticipantId: string;
    userCompaniesHistoryId: string;
    developerType?: string;
    activityName?: string;
  };
  projectSections?: Array<{ name: string; number: string }>;
  titleId: string;
  projectId: string;
  userId: string;
  onSuccess?: () => void;
  setIsSecondLoading?: Dispatch<SetStateAction<boolean>>;
  existingRepresentatives?: Array<{
    userCompaniesHistoryId: string;
    userId: string;
    administrativeDocumentId: string;
  }>;
}

export function useAddRepresentativeWithDocument(
  isArray?: boolean,
  isControls?: boolean,
  isOtherDeveloper?: boolean,
) {
  const { titleId } = useCoordinator();

  const { mutateAsync: postGenericRepresentative } = $journalApi.useMutation(
    "post",
    "/api/v1/journals/title/{titleId}/representatives",
    {
      body: (data: any) => data,
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: journalsQueries.getTitleJournal({ titleId }).queryKey,
        });
        queryClient.invalidateQueries({
          queryKey: journalsQueries.getJournalSignersList(titleId).queryKey,
        });
        notification.success({
          message: NOTIFICATION_MESSAGES.SUCCESS, // "Успешно"
          description: NOTIFICATION_MESSAGES.REPRESENTATIVE_ADDED_DESC, // "Представитель успешно добавлен"
        });
      },
      onError: () => {
        notification.error({
          message: NOTIFICATION_MESSAGES.ERROR, // "Ошибка"
          description: NOTIFICATION_MESSAGES.REPRESENTATIVE_ADD_FAILED_DESC, // "Не удалось добавить представителя"
        });
      },
    },
  );

  const { mutateAsync: postConstructionControlRepresentative } =
    $journalApi.useMutation(
      "post",
      "/api/v1/journals/title/{titleId}/constructionControl",
      {
        body: (data: any) => data,
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: journalsQueries.getTitleJournal({ titleId }).queryKey,
          });
          queryClient.invalidateQueries({
            queryKey: journalsQueries.getJournalSignersList(titleId).queryKey,
          });
          notification.success({
            message: NOTIFICATION_MESSAGES.SUCCESS, // "Успешно"
            description: NOTIFICATION_MESSAGES.REPRESENTATIVE_ADDED_DESC, // "Представитель успешно добавлен"
          });
        },
        onError: () => {
          notification.error({
            message: NOTIFICATION_MESSAGES.ERROR, // "Ошибка"
            description: NOTIFICATION_MESSAGES.REPRESENTATIVE_CONSTRUCTION_ADD_FAILED_DESC, // "Не удалось добавить представителя строительного контроля"
          });
        },
      },
    );

  return useMutation({
    mutationFn: async ({
      administrativeDocumentId,
      titleId,
      userId,
      representative,
      projectSections,
      onSuccess,
      setIsSecondLoading,
      existingRepresentatives = [],
    }: IRequest) => {
      if (setIsSecondLoading) {
        setIsSecondLoading(true);
      }

      const {
        userCompaniesHistoryId,
        projectParticipantId,
        isConstructionControl,
        representativeType,
        developerType,
        companyId,
        activityName,
      } = representative;

      const representativeObject = {
        userCompaniesHistoryId,
        userId,
        administrativeDocumentId,
        projectSections: projectSections ?? undefined,
        ...(isOtherDeveloper ? { activityName } : {}),
      };

      const request = {
        params: {
          path: {
            titleId,
          },
        },
        body: {
          [representativeType]: isArray
            ? [
                {
                  developerType:
                    representativeType === "developer"
                      ? developerType
                      : undefined,
                  companyId,
                  projectParticipantId,
                  ...(isControls
                    ? {
                        constructionControls: [
                          ...existingRepresentatives,
                          representativeObject,
                        ],
                      }
                    : {
                        representatives: [
                          ...existingRepresentatives,
                          representativeObject,
                        ],
                      }),
                },
              ]
            : {
                developerType:
                  representativeType === "developer"
                    ? developerType
                    : undefined,
                companyId,
                projectParticipantId,
                ...(isControls
                  ? {
                      constructionControls: [representativeObject],
                    }
                  : {
                      representatives: [representativeObject],
                    }),
              },
        },
      };

      const response = isConstructionControl
        ? await postConstructionControlRepresentative(request).finally(() => {
            if (setIsSecondLoading) {
              setIsSecondLoading(false);
            }
          })
        : await postGenericRepresentative(request).finally(() => {
            if (setIsSecondLoading) {
              setIsSecondLoading(false);
            }
          });

      const errorsList = response?.errorsList;

      const hasErrors =
        errorsList &&
        Object.values(errorsList).some((entry: any) => {
          if (!entry) return false;
          if (Array.isArray(entry)) {
            return entry.some(
              (subEntry) =>
                subEntry?.representatives &&
                subEntry.representatives.length > 0,
            );
          }
          return (
            Array.isArray(entry.representatives) &&
            entry.representatives.length > 0
          );
        });

      if (hasErrors) {
        const messages: string[] = [];

        Object.values(errorsList).forEach((entry: any) => {
          if (!entry) return;

          const reps = Array.isArray(entry)
            ? entry.flatMap((e) => e.representatives || [])
            : entry.representatives || [];

          reps.forEach((rep: any) => {
            if (rep?.errorMessage) messages.push(rep.errorMessage);
          });
        });

        if (messages.length > 0) {
          notification.error({
            message: messages[0] || "Ошибка добавления представителя",
            description: messages.join("\n"),
          });
          return;
        }
      }

      if (onSuccess && !hasErrors) {
        onSuccess();
      }

      return response;
    },
    onError: (e: any) => {
      if (e.message) notification.error({ message: e.message });
    },
  });
}

